import ServiceBusController from "servicestack/service-bus.controller";
import AutoQuery from "servicestack/auto-query/auto-query";
import modals from "webmodals/modals";
import { HOSTED_PCI_CONFIG } from "emun.payments/hosted-pci-handler";

const backoffBase = 1000; // base delay in ms

export default class PaymentMethodManagerController extends ServiceBusController {
  $onInit() {
    // Service dependencies
    this.$geo = this.$ioc("$geo");
    this.$sce = this.$ioc("$sce");
    this.$location = this.$ioc("$location");
    const parsedQueryString = window.location.search
      .substring(1)
      .split("&")
      .reduce((acc, pair) => {
        const [key, value] = pair.split("=");
        acc[decodeURIComponent(key)] = decodeURIComponent(value || "");
        return acc;
      }, {});
    const qs = this.$location.search();
    const { retailerId, vendorId, apiKey } = { ...parsedQueryString, ...qs };
    this.retailerId = this.retailerId || retailerId || "9999999999";
    this.vendorId = this.vendorId || vendorId || "141";
    this.apiKey = this.apiKey || apiKey || "ALD-818";
    console.debug("PaymentMethodManager initialized with", {
      qs,
      parsedQueryString,
      retailerId,
      vendorId,
      apiKey,
      $ctrl: this,
      $search: window.location.search,
    }); // Component state
    this.paymentMethod = this._createEmptyPaymentMethod();
    this.selectedPaymentMethodId = null;
    this.isAddingNew = false;
    this.sameAsCorporate = true;
    this.retailer = null;
    this.newCardID = null;
    this.iframeReady = false; // Track iframe ready state
    this.preventAutoSubmit = true; // Prevent automatic token submission on load

    this.TOKENIZATION_PROVIDER = HOSTED_PCI_CONFIG.PROVIDER;
    this.HPCI_SITE_ID = HOSTED_PCI_CONFIG.SITE_ID;

    // Listen for credit card creation events
    this.$on("creditCardCreated", (e, args) => {
      this.newCardID = args.ID || args.id;
      this.paymentMethodsQuery.load(true);
    });
    this._loadRetailerInfo().then(() => {
      // Initialize HostedPCI configuration
      this._initializeHostedPCI();
      this._initializeExpirationOptions();

      // Set up HostedPCI event listeners
      this._setupHostedPCIListeners();
      this._initializePaymentMethods();
    });

    // run _postDimensions every 2 sections, kill in onDestroy    this.sizingInterval = setInterval(_postDimensions, 88888);
  }

  // Private helper methods
  _createEmptyPaymentMethod() {
    return {
      id: "",
      number: "",
      name: "",
      expirationMonth: "",
      expirationYear: "",
      expiration: "",
      expiresOn: "",
      lastFour: "",
      provider: "",
      network: "",
      country: "US",
      retailerId: this.retailerId,
    };
  }

  _initializeHostedPCI() {
    const hostName = this._buildHostName();
    const iframeParams = this._buildIframeParams(hostName);
    this.hpciIframeUrl = this.$sce.trustAsResourceUrl(
      `https://cci.framehpci.com/iSynSApp/showPxyPage!ccFrame.action?${iframeParams}`
    );
  }

  _buildHostName() {
    let hostName = `${window.location.protocol}//${window.location.hostname}`;
    if (hostName.indexOf("localhost") > -1) {
      hostName += `:${window.location.port}`;
    }
    return hostName;
  }

  _buildIframeParams(hostName) {
    const params = new URLSearchParams({
      pgmode1: "prod",
      locationName: "checkout1",
      sid: this.HPCI_SITE_ID,
      reportCCType: "Y",
      reportCCDigits: "Y",
      formatCCDigits: "Y",
      formatCCDigitsDelimiter: "-",
      formatCCMaskMode: "S6X6",
      formatCCMaskChar: "*",
      enableEarlyToken: "Y",
      reportInit: "Y",
      fullParentHost: hostName,
      fullParentQStr: "/#/payment-method-manager",
      border: "0",
      padding: "0",
      margin: "0",
      background: "transparent",
    });
    return params.toString();
  }

  _initializeExpirationOptions() {
    // Generate months (01-12)
    this.months = Array.from({ length: 12 }, (_, i) =>
      (i + 1).toString().padStart(2, "0")
    );

    // Generate years (current + 20 years)
    const currentYear = new Date().getFullYear();
    this.years = Array.from({ length: 20 }, (_, i) =>
      (currentYear + i).toString()
    );
  }
  _initializePaymentMethods(retryCount = 0, maxRetries = 3) {
    if (!this.retailerId) {
      console.warn("PaymentMethodManager: No retailerId provided");
      return;
    }

    const paymentMethodsUrl = `QueryPaymentMethods/Wholesale/${this.retailerId}.json?apiKey=${this.apiKey}&JsConfig=ExcludeDefaultValues`;

    this.paymentMethodsQuery = new AutoQuery({
      url: paymentMethodsUrl,
      autoLoad: true,
      scrub: (res) => {
        // Filter for HostedPCI tokens only
        res.data.results = res.data.results.filter(
          (cc) => cc.tokenizationProvider === this.TOKENIZATION_PROVIDER
        );
      },
      onLoaded: (args) => this._handlePaymentMethodsLoaded(args),
      onFailed: (args) => {
        if (retryCount < maxRetries) {
          // Exponential backoff: 1s, 2s, 4s, 8s...
          const delayMs = Math.pow(2, retryCount) * backoffBase;
          console.warn(
            `Failed to load payment methods (attempt ${retryCount + 1}/${
              maxRetries + 1
            }). Retrying in ${delayMs}ms...`,
            args
          );
          setTimeout(() => {
            // Dispose the failed query and reinitialize
            if (this.paymentMethodsQuery) {
              this.paymentMethodsQuery.dispose();
            }
            this._initializePaymentMethods(retryCount + 1, maxRetries);
          }, delayMs);
        } else {
          console.error(
            "Failed to load payment methods after all retries:",
            args
          );
          modals.alert(
            "Unable to load payment methods. Please refresh the page to try again."
          );
        }
      },
    });
  }
  _loadRetailerInfo(retryCount = 0, maxRetries = 3) {
    if (!this.retailerId) return Promise.reject("no retailerId");

    return this.bus
      .send(
        `QueryRetailers.json?apiKey=${this.apiKey}&RetailerId=${this.retailerId}`
      )
      .then((res) => {
        if (res.data.results.length > 0) {
          this.retailer = res.data.results[0];
          _postDimensions();
        }
      })
      .catch((error) => {
        if (retryCount < maxRetries) {
          // Exponential backoff: 1s, 2s, 4s, 8s...
          const delayMs = Math.pow(2, retryCount) * backoffBase;
          console.warn(
            `Failed to load retailer info (attempt ${retryCount + 1}/${
              maxRetries + 1
            }). Retrying in ${delayMs}ms...`,
            error
          );
          return new Promise((resolve) => setTimeout(resolve, delayMs)).then(
            () => this._loadRetailerInfo(retryCount + 1, maxRetries)
          );
        }
        console.error("Failed to load retailer info after all retries:", error);
        throw error;
      });
  }
  _handlePaymentMethodsLoaded(args) {
    // Filter expired cards
    args.results = args.results.filter(
      (cc) => cc.meta && cc.meta.IsExpired !== "True"
    );
    args.total = args.results.length;

    if (args.total === 1) {
      this.selectCC(args.first);
    } else if (args.total > 1) {
      this._selectPrimaryOrFirstCard(args.results);
    } else {
      this._showNewCardForm();
    }

    // Handle new card selection if specified
    this._handleNewCardSelection(args.results);
  }

  _selectPrimaryOrFirstCard(results) {
    let selectedCard =
      results.find((cc) => cc.meta && cc.meta.IsPrimary === "True") ||
      results[0];
    if (selectedCard) {
      this.selectCC(selectedCard);
    }
  }

  _showNewCardForm() {
    this.paymentMethod.country = "US";
    this.selectedPaymentMethodId = "ADD_NEW";
    this.isAddingNew = true;
    if (this.sameAsCorporate) {
      this._copyBillingAddress();
    }
  }

  _handleNewCardSelection(results) {
    if (!this.newCardID) return;

    const selectedCard = results.find((cc) => cc.token === this.newCardID);
    if (selectedCard) {
      this.selectCC(selectedCard);
    }
  }
  /**
   * Check if HostedPCI iframe is ready for input
   */
  isIframeReady() {
    return this.iframeReady && HOSTED_PCI_CONFIG.ready;
  }

  /**
   * Called when user selects a payment method from dropdown
   */
  onPaymentMethodSelected() {
    if (this.selectedPaymentMethodId === "ADD_NEW") {
      this.isAddingNew = true;
      this.paymentMethod = this._createEmptyPaymentMethod();
      if (this.sameAsCorporate) {
        this._copyBillingAddress();
      }
      _postDimensions();
    } else {
      this.isAddingNew = false;
      const selectedCard = this.paymentMethodsQuery.results.find(
        (card) => card.id === this.selectedPaymentMethodId
      );
      console.debug(
        "Selected existing card:",
        this.selectedPaymentMethodId,
        selectedCard || this.paymentMethodsQuery.results
      );
      if (selectedCard) {
        // Populate all form fields with selected card data
        this.selectCC(selectedCard);

        // Parse expiration date if needed
        if (selectedCard.expiresOn) {
          const expiryDate = new Date(selectedCard.expiresOn);
          this.paymentMethod.expirationMonth = (expiryDate.getMonth() + 1)
            .toString()
            .padStart(2, "0");
          this.paymentMethod.expirationYear = expiryDate
            .getFullYear()
            .toString();
        } else if (
          selectedCard.expiration &&
          selectedCard.expiration.length >= 6
        ) {
          // Format: MMYYYY
          this.paymentMethod.expirationMonth =
            selectedCard.expiration.substring(0, 2);
          this.paymentMethod.expirationYear = selectedCard.expiration.substring(
            2,
            6
          );
        }
      }
      _postDimensions();
    }
  }
  /**
   * Submit existing card selection
   */
  submitExistingCard() {
    const selectedCard = this.paymentMethodsQuery.results.find(
      (card) => card.id === this.selectedPaymentMethodId
    );
    if (!selectedCard) {
      console.warn("No card selected for submission");
      return;
    }

    // Disable auto-prevent to allow message posting
    this.preventAutoSubmit = false;

    // Resolve gateway token before submitting
    console.log("Resolving gateway token for card:", selectedCard.id);
    this._resolveGatewayToken(selectedCard).catch((error) =>
      this._handleResolveGatewayTokenError(error)
    );
  }

  /**
   * Resolve gateway token with retry logic for existing card
   */ _resolveGatewayToken(selectedCard, retryCount = 0, maxRetries = 2) {
    const retryDelay = 3000; // 3 seconds between retries

    return this.bus
      .read(`HostedPCI/GetGatewayToken.json?apiKey=${this.apiKey}`, {
        retailerId: this.retailerId,
        vendorId: this.vendorId,
        token: selectedCard.id,
      })
      .then((response) => {
        const { gatewayResponseCode } = response.data;

        // Check for duplicate transaction error
        if (gatewayResponseCode === "E00027" && retryCount < maxRetries) {
          console.warn(
            `Duplicate transaction detected (attempt ${retryCount + 1}/${
              maxRetries + 1
            }). Retrying in ${retryDelay}ms...`
          );

          return new Promise((resolve) => {
            setTimeout(() => {
              resolve(
                this._resolveGatewayToken(
                  selectedCard,
                  retryCount + 1,
                  maxRetries
                )
              );
            }, retryDelay);
          });
        }

        return this._handleResolveGatewayTokenSuccess(
          response.data,
          selectedCard
        );
      })
      .catch((error) => this._handleResolveGatewayTokenError(error));
  }

  /**
   * Delete the currently selected card
   */
  deleteSelectedCard() {
    const selectedCard = this.paymentMethodsQuery.results.find(
      (card) => card.id === this.selectedPaymentMethodId
    );
    if (selectedCard) {
      this.deleteCard(selectedCard);
    }
  }

  /**
   * Cancel adding new card
   */
  cancelNewCard() {
    this.isAddingNew = false;
    this.paymentMethod = this._createEmptyPaymentMethod();

    // Select first available card or go back to "ADD_NEW"
    if (this.paymentMethodsQuery.total > 0) {
      const firstCard = this.paymentMethodsQuery.results[0];
      this.selectCC(firstCard);
    } else {
      this.selectedPaymentMethodId = "ADD_NEW";
    }

    _postDimensions();
  }
  selectCC(selectedCard) {
    // This method is kept for backwards compatibility but is now handled by dropdown
    if (
      !selectedCard ||
      !this.paymentMethodsQuery ||
      !this.paymentMethodsQuery.results
    ) {
      console.warn("Invalid card selection");
      return;
    }
    this.selectedPaymentMethodId = selectedCard.id;
    Object.assign(this.paymentMethod, selectedCard);
    this.paymentMethod.name = selectedCard.personName.full;
    this.paymentMethod.expirationMonth = selectedCard.expirationMonth
      .toString()
      .padStart(2, "0");
    this.paymentMethod.expirationYear = selectedCard.expirationYear.toString();
    this.isAddingNew = false;

    // Note: Gateway token resolution is now deferred until user explicitly submits
    // This prevents automatic token submission on page load
    console.log("Card selected, awaiting user submission:", selectedCard.id);
  }
  toggleSameAsCorporate() {
    if (
      !this.sameAsCorporate ||
      !this.retailer ||
      !this.retailer.billingLocation
    ) {
      return console.warn(
        "No corporate billing address to copy",
        this.retailer
      );
    }

    console.debug("toggleSameAsCorporate", this.retailer);
    const { billingLocation } = this.retailer;
    Object.assign(this.paymentMethod, {
      address: billingLocation.address,
      address2: billingLocation.address2,
      city: billingLocation.city,
      state: billingLocation.state,
      zip: billingLocation.zip,
      country: billingLocation.country,
    });
  }
  newCard() {
    this.sameAsCorporate = true;
    this.isAddingNew = true;
    this.selectedPaymentMethodId = "ADD_NEW";
    this.paymentMethod = this._createEmptyPaymentMethod();
    this.hpciData = null; // Reset tokenization data
    this.iframeReady = false; // Reset iframe ready state

    // Copy retailer billing address if available and checkbox is checked
    if (this.sameAsCorporate) {
      this._copyBillingAddress();
    }

    console.log("New card form initialized, waiting for iframe ready state");
    _postDimensions();
  }
  _copyBillingAddress() {
    if (!this.retailer || !this.retailer.billingLocation) return;

    const billingLocation = this.retailer.billingLocation;
    Object.assign(this.paymentMethod, {
      address: billingLocation.address,
      address2: billingLocation.address2,
      city: billingLocation.city,
      state: billingLocation.state,
      zip: billingLocation.zip,
      country: billingLocation.country || "US",
    });
  }
  saveCard() {
    if (!this._validateCardData()) {
      return;
    }

    const hostedPciData = this._extractHostedPciData();
    if (!hostedPciData.isValid) {
      modals.alert(
        "Invalid credit card information. Please check your card details."
      );
      return;
    }

    const paymentMethodData = this._buildPaymentMethodData(hostedPciData);

    this.bus
      .send(
        `CreateRetailerPaymentMethod.json?apiKey=${this.apiKey}&JsConfig=ExcludeDefaultValues`,
        paymentMethodData
      )
      .then((response) => this._handleSaveSuccess(response))
      .catch((error) => this._handleSaveError(error));
  }

  _validateCardData() {
    const requiredFields = [
      "name",
      "expirationMonth",
      "expirationYear",
      "address",
      "city",
      "state",
      "zip",
      "country",
    ];
    const missingFields = requiredFields.filter(
      (field) => !this.paymentMethod[field]
    );

    if (missingFields.length > 0) {
      modals.alert(
        `Please fill in all required fields: ${missingFields.join(", ")}`
      );
      return false;
    }

    return true;
  }

  _extractHostedPciData() {
    const { hpciCC, hpciCCType, hpciCCLast4 } = this.hpciData || {};
    const elements = {
      token: hpciCC,
      type: hpciCCType,
      lastFour: hpciCCLast4,
    };

    console.warn("Extracted HostedPCI data:", elements, this.hpciData);

    // Check if elements exist and have values
    const isValid = Object.values(elements).every((el) => el && el.length > 0);
    return {
      isValid,
      ...elements,
    };
  }
  _buildPaymentMethodData(hostedPciData) {
    return {
      ...this.paymentMethod,
      tokenizationProvider: this.TOKENIZATION_PROVIDER,
      tokenizationCode: hostedPciData.token,
      number: "TOKEN",
      type: hostedPciData.type,
      network: hostedPciData.type,
      provider: hostedPciData.type,
      lastFour: hostedPciData.lastFour,
      isPrimary: true,
      retailerId: this.retailerId,
      expiration:
        this.paymentMethod.expirationMonth + this.paymentMethod.expirationYear,
      expiresOn: `${this.paymentMethod.expirationMonth}/1/${this.paymentMethod.expirationYear}`,
    };
  }
  _handleResolveGatewayTokenSuccess(data, selectedCard) {
    console.log("Vendor gateway token resolved:", data);
    const { gatewayToken, status, gatewayResponseStatus, gatewayResponseCode } =
      data;
    selectedCard.gatewayTokenResponse = data;

    // Check for duplicate transaction error (E00027)
    if (gatewayResponseCode === "E00027") {
      const errorMessage =
        "This card was recently used. Please try again in a few moments or use a different payment method.";
      console.warn(errorMessage, data);
      return modals.alert(errorMessage);
    }

    if (status !== "success" || !gatewayToken) {
      const errorMessage = `Vendor gateway token resolution failed: ${
        gatewayResponseStatus || gatewayResponseCode || "Unknown error"
      }`;
      console.error(errorMessage, data);
      return modals.alert(errorMessage);
    }

    this.gatewayToken = gatewayToken;
    selectedCard.gatewayToken = gatewayToken;

    // Only post message if not in auto-prevent mode
    if (!this.preventAutoSubmit) {
      _postMessage(selectedCard);
    } else {
      console.log("Gateway token ready, waiting for user action");
    }
  }

  _handleResolveGatewayTokenError(error) {
    console.error("Vendor gateway token resolution failed:", error);
    const errorMessage =
      (error && error.data && error.data.message) ||
      "Vendor gateway token resolution failed. Please try again.";
    modals.alert(errorMessage);
    _postError(errorMessage, error);
  }

  _handleSaveSuccess(response) {
    console.log("Payment method saved successfully:", response.data);
    this.newCardID = response.data.tokenizationCode;
    this.$broadcast("creditCardCreated", {
      id: response.data.tokenizationCode,
    });
    // Switch back to showing the new card
    this.isAddingNew = false;
  }
  _handleSaveError(error) {
    console.error("Save card failed:", error);
    const errorMessage =
      (error && error.data && error.data.message) ||
      "Failed to save credit card. Please try again.";
    modals.alert(errorMessage);
    _postError(errorMessage, error);
  }
  deleteCard(paymentMethod) {
    if (!paymentMethod || !paymentMethod.lastFour || !paymentMethod.id) {
      console.warn("Invalid payment method for deletion");
      return;
    }

    const confirmMessage = `Are you sure you want to delete card ending in ${paymentMethod.lastFour}?`;

    return modals
      .confirm(confirmMessage, "Delete Payment Method")
      .result.then(() => this._executeCardDeletion(paymentMethod))
      .catch(() => {
        // User cancelled - no action needed
        console.log("Card deletion cancelled by user");
      });
  }
  _executeCardDeletion(paymentMethod) {
    const request = {
      Id: paymentMethod.id,
      RetailerId:
        (paymentMethod.meta && paymentMethod.meta.RetailerId) ||
        this.retailerId,
    };

    this.bus
      .send(
        `DeleteRetailerPaymentMethod.json?apiKey=${this.apiKey}&JsConfig=ExcludeDefaultValues`,
        request
      )
      .then(() => {
        console.log("Payment method deleted successfully");
        this.paymentMethodsQuery.load(true);
        // After deletion, show add new form if no cards left
        if (this.paymentMethodsQuery.total === 0) {
          this.selectedPaymentMethodId = "ADD_NEW";
          this.isAddingNew = true;
        }
      })
      .catch((error) => {
        console.error("Delete card failed:", error);
        const errorMessage =
          (error && error.data && error.data.message) ||
          "Failed to delete credit card. Please try again.";
        modals.alert(errorMessage);
      });
  }

  /**
   * Component cleanup method
   * Called when the component is destroyed
   */
  $onDestroy() {
    // Clean up any event listeners
    if (this.hostedPCIEventListeners) {
      this.hostedPCIEventListeners.forEach((listener) => {
        window.removeEventListener(listener.event, listener.handler);
      });
    }

    // Cancel any pending HTTP requests
    if (this.bus && typeof this.bus.cancel === "function") {
      this.bus.cancel();
    }

    // Clear sizing interval
    if (this.sizingInterval) {
      clearInterval(this.sizingInterval);
      this.sizingInterval = null;
    }
    console.log("PaymentMethodManager component destroyed and cleaned up");
  }
  /**
   * Set up HostedPCI event listeners for enhanced integration
   */
  _setupHostedPCIListeners() {
    this.hostedPCIEventListeners = [];

    // Listen for tokenization completion
    const tokenizationHandler = (event) => {
      this.hpciData = event.detail;
      console.log("HostedPCI tokenization completed", event.detail);

      // Apply changes in Angular context
      this.$scope.$apply(() => {
        // Only auto-process if we're in adding new card mode
        if (this.isAddingNew) {
          console.log("Auto-processing tokenization for new card");
        }
      });
    };

    // Listen for validation errors
    const errorHandler = (event) => {
      console.warn("HostedPCI validation error", event.detail);
      this.$scope.$apply(() => {
        modals.alert(
          "Please check your credit card information and try again."
        );
      });
    };

    // Listen for iframe ready state
    const readyHandler = (event) => {
      console.log("HostedPCI iframe ready", event.detail);
      this.$scope.$apply(() => {
        this.iframeReady = true;
        console.log("HostedPCI iframe is ready for user input");
      });
    };

    // Add event listeners
    // Store references for cleanup
    this.hostedPCIEventListeners.push(
      {
        event: HOSTED_PCI_CONFIG.CUSTOM_EVENTS.TOKENIZATION_COMPLETE,
        handler: tokenizationHandler,
      },
      {
        event: HOSTED_PCI_CONFIG.CUSTOM_EVENTS.VALIDATION_ERROR,
        handler: errorHandler,
      },
      { event: HOSTED_PCI_CONFIG.CUSTOM_EVENTS.READY, handler: readyHandler }
    );

    this.hostedPCIEventListeners.forEach(({ event, handler }) => {
      console.log(`Listening for HostedPCI event: ${event}`);
      window.addEventListener(event, handler);
    });
  }
}

const _postError = (error, ...others) => {
  if (!window.parent) return;
  // Post back to parent
  window.parent.postMessage(
    {
      type: "HPCI_TOKEN",
      token: error,
      status: "error",
      others,
    },
    "*"
  );
  _postDimensions();
};

const _postMessage = (selectedCard, ...others) => {
  if (!window.parent) return;
  // Post back to parent
  window.parent.postMessage(
    {
      type: "HPCI_TOKEN",
      token: selectedCard.gatewayToken,
      selectedCard,
      status: "success",
      others,
    },
    "*"
  );
  _postDimensions();
};

const _postDimensions = (delay = 188) =>
  setTimeout(() => {
    if (!window.parent) return;
    const container = document.getElementsByTagName("payment-method-manager");
    // Post back to parent
    window.parent.postMessage(
      {
        type: "DIMENSIONS",
        height:
          container && container.length > 0 ? container[0].offsetHeight : 800,
        width:
          container && container.length > 0 ? container[0].offsetWidth : 600,
      },
      "*"
    );
  }, delay);
